import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ["#083CA6", "#00883A", "#F59E0B", "#EF4444"];
const LEGEND_STYLE = { fontSize: "12px" };
const pieLabel = (e) => `${e.name}: ${e.value}`;
const pieTooltip = (v, n, p) => [`${v} vagas · ${p.payload.inscritos} inscritos`, n];

export default function EmpreendimentoWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/empreendimentos/publicado`)
      .then((r) => setData(r.data))
      .catch(() => setData({ empreendimento: null }))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data?.empreendimento) return null;

  const { empreendimento: emp, stats } = data;
  const pie = [
    { name: "Bolsa Família", value: emp.vagas_bolsa_familia, inscritos: stats?.bolsa_familia || 0 },
    { name: "PNE", value: emp.vagas_pne, inscritos: stats?.pne || 0 },
    { name: "Idoso", value: emp.vagas_idoso, inscritos: stats?.idoso || 0 },
    { name: "Vulnerabilidades", value: emp.vagas_vulnerabilidades, inscritos: stats?.vulnerabilidades || 0 },
  ].filter((d) => d.value > 0);

  return (
    <section className="max-w-7xl mx-auto px-4 py-14" data-testid="empreendimento-widget">
      <div className="text-center mb-8">
        <div className="text-xs uppercase tracking-wider text-[#00883A] font-bold mb-1">Empreendimento em Seleção</div>
        <h2 className="text-3xl md:text-4xl font-black text-[#0F172A] flex items-center justify-center gap-3">
          <Building2 className="w-8 h-8 text-[#083CA6]" />{emp.nome}
        </h2>
        <div className="mt-3 flex flex-wrap justify-center gap-2">
          <Badge className="bg-[#083CA6] text-white text-sm px-4 py-1">
            {emp.vagas_total} vagas disponíveis
          </Badge>
          {stats && (
            <Badge className="bg-[#00883A] text-white text-sm px-4 py-1">
              {stats.total_inscritos} inscritos até o momento
            </Badge>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-3 max-w-2xl mx-auto">
          As inscrições são ilimitadas. As vagas do empreendimento serão preenchidas por sorteio conforme critérios,
          e os demais inscritos concorrerão em futuros empreendimentos.
        </p>
      </div>

      {emp.descricao && (
        <div className="max-w-3xl mx-auto mb-6 p-5 bg-white border border-slate-200 rounded-lg">
          <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{emp.descricao}</p>
        </div>
      )}

      {emp.fotos?.length > 0 && (
        <div className="max-w-5xl mx-auto mb-6">
          <h3 className="text-sm font-bold text-slate-700 mb-3">Galeria do empreendimento</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {emp.fotos.map((f) => (
              <a
                key={f.id}
                href={`${API}/empreendimentos/${emp.id}/fotos/${f.id}`}
                target="_blank" rel="noopener noreferrer"
                className="aspect-square rounded overflow-hidden border hover:opacity-90 transition-opacity"
              >
                <img
                  src={`${API}/empreendimentos/${emp.id}/fotos/${f.id}`}
                  alt="empreendimento" className="w-full h-full object-cover" loading="lazy"
                />
              </a>
            ))}
          </div>
        </div>
      )}

      <Card>
        <CardContent className="p-6 grid md:grid-cols-2 gap-6 items-center">
          <div className="space-y-3">
            <h3 className="font-bold text-[#083CA6]">Distribuição de vagas por categoria</h3>
            {pie.map((d, i) => {
              const pct = emp.vagas_total ? Math.round((d.value / emp.vagas_total) * 100) : 0;
              return (
                <div key={d.name} className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ background: COLORS[i] }} />
                  <div className="flex-1">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">{d.name}</span>
                      <span className="text-slate-600">
                        <strong className="text-[#083CA6]">{d.value}</strong> vagas · {d.inscritos} inscritos
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2 mt-1">
                      <div className="h-2 rounded-full" style={{ width: `${pct}%`, background: COLORS[i] }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={pieLabel}>
                  {pie.map((slice) => <Cell key={slice.name} fill={COLORS[pie.indexOf(slice)]} />)}
                </Pie>
                <Tooltip formatter={pieTooltip} />
                <Legend wrapperStyle={LEGEND_STYLE} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
